import React from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import { defaultImage } from '@assets'
import { Icon, Text } from '../atoms'

interface NoteContactCardProps {
  name: string
  avatarUrl?: string | null
  // « il y a 6 jours », or null when nothing was exchanged yet.
  lastExchange: string | null
  otherNotesCount: number
  onOpenProfile: () => void
  onOpenNotes: () => void
  // Left out when the person of the note can't be changed.
  onChange?: () => void
}

// « À propos de » card of the note editor: who the note is about, when you
// last talked and how many other notes you have on them.
export const NoteContactCard = ({
  name,
  avatarUrl,
  lastExchange,
  otherNotesCount,
  onOpenProfile,
  onOpenNotes,
  onChange,
}: NoteContactCardProps) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const firstName = name.split(' ')[0]

  return (
    <View testID="note-about-card" style={styles.card}>
      <View style={styles.header}>
        <Image source={avatarUrl ? { uri: avatarUrl } : defaultImage} style={styles.avatar} />
        <View style={styles.identity}>
          <Text type="bodyBold" numberOfLines={1}>
            {name}
          </Text>
          <TouchableOpacity testID="note-about-profile" onPress={onOpenProfile} style={styles.row}>
            <Text type="bodyExtraSmall" color={theme.colors.primary}>
              {t('notesForm.seeProfile')}
            </Text>
            <Icon name="arrow" size={14} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>
        {onChange && (
          <TouchableOpacity testID="note-about-change" onPress={onChange} style={styles.change}>
            <Text type="bodySmall" color={theme.colors.primary}>
              {t('notesForm.change')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.divider} />
      <View style={styles.infoRow}>
        <Icon name="messageCircle" size={16} color={theme.colors.greyBlue} />
        <Text type="bodySmall" color={theme.colors.text} style={styles.infoText}>
          {lastExchange ? (
            <>
              {t('notesForm.lastExchange')}{' '}
              <Text type="bodySmallBold" color={theme.colors.text}>
                {lastExchange}
              </Text>
            </>
          ) : (
            t('notesForm.noExchangeYet')
          )}
        </Text>
      </View>
      <View style={styles.infoRow}>
        <Icon name="note" size={16} color={theme.colors.greyBlue} />
        <Text type="bodySmall" color={theme.colors.text} style={styles.infoText}>
          {t('notesForm.otherNotes', { count: otherNotesCount, name: firstName })}
        </Text>
        {otherNotesCount > 0 && (
          <TouchableOpacity testID="note-about-notes" onPress={onOpenNotes}>
            <Text type="bodySmall" color={theme.colors.primary}>
              {t('notesForm.see')}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      borderWidth: 1,
      borderColor: theme.colors.grey20,
      borderRadius: theme.border.large,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: 14,
      marginTop: theme.spacing.sm,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
    },
    avatar: {
      width: 44,
      height: 44,
      borderRadius: 22,
    },
    identity: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    },
    change: {
      alignSelf: 'flex-start',
    },
    divider: {
      height: 1,
      backgroundColor: theme.colors.grey20,
      marginTop: theme.spacing.md,
      marginBottom: 10,
    },
    infoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    infoText: {
      flex: 1,
    },
  })
